import { useCallback, useMemo } from 'react';
import {
  addDays,
  format,
  fromUnixTime,
  min,
  parse,
} from 'date-fns';
import { store } from 'redux/store';
import { useSelector } from 'react-redux';
import {
  selectMeteorologyAgroWBSequentialDVPeriodEnd,
  selectMeteorologyAgroWBSequentialDVPeriodStart,
  setPeriodStart,
  setPeriodEnd,
} from 'redux/reducers/meteorologyAgroWBSequentialDVReducer';
import useOptions from './useOptions';

export default function usePeriod() {
  const { options } = useOptions();
  const periodStart = useSelector(selectMeteorologyAgroWBSequentialDVPeriodStart);
  const periodEnd = useSelector(selectMeteorologyAgroWBSequentialDVPeriodEnd);
  const startDate = useMemo(
    () => (periodStart ? parse(periodStart, 'yyyy-MM-dd', new Date()) : undefined),
    [periodStart],
  );
  const endDate = useMemo(
    () => (periodEnd ? parse(periodEnd, 'yyyy-MM-dd', new Date()) : undefined),
    [periodEnd],
  );

  const dateIntervals = useMemo(() => {
    if (!options) return [];

    const start = fromUnixTime(options.startDate);
    const maxEndDate = fromUnixTime(options.endDate);
    const end = (startDate && !endDate)
      ? min([maxEndDate, addDays(startDate, options.maxInterval - 1)])
      : maxEndDate;

    return [{ start, end }];
  }, [options, startDate, endDate]);

  const changePeriod = useCallback((newDates: (Date | null)[]) => {
    const [newStartDate, newEndDate] = newDates;
    const newPeriodStart = newStartDate ? format(newStartDate, 'yyyy-MM-dd') : undefined;
    const newPeriodEnd = newEndDate ? format(newEndDate, 'yyyy-MM-dd') : undefined;
    store.dispatch(setPeriodStart(newPeriodStart));
    store.dispatch(setPeriodEnd(newPeriodEnd));
  }, []);

  return {
    startDate,
    endDate,
    dateIntervals,
    changePeriod,
  };
}
